// **********************************************************************
//
// MediaElementTrack
//
// This is the base track that provides an interface to a
// HTMLMediaElement (<video> or <audio>)
//
// **********************************************************************
import EventEmitter from '@/helpers/EventEmitter'

const Events = {
  LOAD: 'load',
  END: 'end',
  PLAY: 'play',
  PAUSE: 'pause',
  STATE: 'state'
}

class MediaElementTrack {
  constructor(trackConfig, tag, defaultAttr) {
    Object.assign(this, EventEmitter)

    // CONFIG
    this.id = trackConfig.id
    this.config = JSON.parse(JSON.stringify(trackConfig))

    // LOAD STATE
    this.loading = {
      progress: 0,
      ready: false,
      completed: false
    }

    this.state = '';
    this.Events = Events

    // ELEMENT
    this.el = document.createElement(tag)

    // ATTRIBUTES
    this.config.attr = Object.assign(defaultAttr, this.config.attr)
    this.config.attr['webkit-playsinline'] = this.config.attr.playsinline
    delete this.config.attr['loop']
    delete this.config.attr['controls']

    for (const [attr, value] of Object.entries(this.config.attr)) {
      if (value) this.el.setAttribute(attr, value)
    }
  }

  load = () => {
    return new Promise(resolve => {
      let el = this.el
      // el.crossOrigin = this.config.crossOrigin || 'anonymous'

      const onReady = () => {
        el.pause()
        el.currentTime = 0
        // console.log('onReady', el.tagName, this.loading)
        this.onLoaded()
        resolve()
      }

      const onReadyStateChange = () => {
        if (el.readyState < 4) return
        el.oncanplaythrough = null
        el.onreadystatechange = null
        this.loading.ready = true
        if (this.config.preload === 'buffer') onReady()
      }

      const onProgress = evt => {
        let progress = 0
        if (evt && evt.lengthComputable) {
          progress = evt.loaded / evt.total
        } else if (el.buffered.length > 0) {
          progress = el.buffered.end(0) / el.duration
        }
        this.loading.progress = progress

        if (progress >= 1) {
          el.onprogress = null
          el.ontimeupdate = null
          this.loading.completed = true

          if (this.config.preload === 'full') onReady()
        }
      }

      el.oncanplaythrough = (evt) => {
        onReadyStateChange(evt)
      }
      el.onreadystatechange = (evt) => {
        onReadyStateChange(evt)
      }
      el.onprogress = (evt) => {
        onProgress(evt)
      }
      el.ontimeupdate = (evt) => {
        onProgress(evt)
      }
      el.onended = (evt) => {
        this.onEnded(evt)
      }

      el.muted = true
      el.src = this.config.src[0].url
      el.load()
      el.play()

      if (this.config.preload === 'no') {
        onReady()
      }
    })
  }

  _setState = (type) => {
    this.state = type
    this.emit(type)
    this.emit(Events.STATE, type)
  }

  onLoaded = () => {
    this._setState(Events.LOAD)
  }

  onEnded = () => {
    this._setState(Events.END)
  }

  play = () => {
    this.el.muted = false
    this.el.play()
    this._setState(Events.PLAY)
  }

  pause = () => {
    this.el.pause()
    this._setState(Events.PAUSE)
  }

  get volume() {
    return this.el.volume
  }

  set volume(val) {
    this.el.volume = val
  }

  get muted() {
    return this.el.muted
  }

  set muted(val) {
    this.el.muted = val
  }

  get currentTime() {
    return this.el.currentTime
  }

  set currentTime(val) {
    this.el.currentTime = val
  }

  get duration() {
    return this.el.duration
  }

  get paused() {
    return this.el.paused
  }

  get ended() {
    return this.el.ended
  }
}

export default MediaElementTrack
